import { useRef, useState } from "react";
import imageCompression from "browser-image-compression";
import { Button } from "./Button.js";
import { Checkbox } from "./Field.js";
import { track } from "../shared/analytics.js";
import styles from "./PhotoUploader.module.css";

interface Props {
  passportId: string;
  refCode: string;
  currentPhotoUrl: string | null;
  onUploaded: (newUrl: string) => void;
}

// Client-side compression target — the server also normalises via sharp,
// but pre-compressing here cuts a 5 MB HEIC down to ~200 KB before it
// even hits the mobile connection, which matters on 3G / poor WiFi at
// the venue.
const COMPRESSION_OPTIONS = {
  maxSizeMB: 0.5,           // ≤ 500 KB
  maxWidthOrHeight: 1024,   // final image never larger than 1024px on the long side
  useWebWorker: true,
  initialQuality: 0.8,
};

export function PhotoUploader(props: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function handleFile(file: File) {
    setError("");
    setBusy(true);
    track({
      name: "passport_photo_pick",
      passportId: props.passportId,
      props: { replaced: !!props.currentPhotoUrl, bytes: file.size },
    });
    try {
      const compressed = await imageCompression(file, COMPRESSION_OPTIONS);
      const dataUrl = await imageCompression.getDataUrlFromFile(compressed);
      setPreview(dataUrl);
      // File stays in the input element; we upload on "Загрузить".
    } catch (e) {
      setError(e instanceof Error ? e.message : "Не смогли обработать фото");
    } finally {
      setBusy(false);
    }
  }

  async function upload() {
    const input = inputRef.current;
    const file = input?.files?.[0];
    if (!file) {
      setError("Выберите файл");
      return;
    }
    if (!consent && !props.currentPhotoUrl) {
      setError("Отметьте согласие на хранение фото");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const compressed = await imageCompression(file, COMPRESSION_OPTIONS);
      const form = new FormData();
      form.append("file", compressed, compressed.name || "photo.jpg");
      form.append("ref", props.refCode);
      if (!props.currentPhotoUrl) form.append("consent", "true");

      const res = await fetch(
        `/api/passport/${encodeURIComponent(props.passportId)}/photo`,
        { method: "POST", body: form, credentials: "same-origin" },
      );
      const json = (await res.json()) as
        | { ok: true; data: { url: string } }
        | { ok: false; error: { code: string; message: string } };
      if (!json.ok) throw new Error(json.error.message || json.error.code);
      props.onUploaded(json.data.url);
      setPreview(null);
      if (input) input.value = "";
    } catch (e) {
      setError(e instanceof Error ? e.message : "Не смогли загрузить");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={styles.wrap}>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="user"
        className={styles.hiddenInput}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleFile(f);
        }}
      />

      {preview ? (
        <>
          <img src={preview} alt="Предпросмотр" className={styles.preview} />
          {!props.currentPhotoUrl && (
            <Checkbox
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
            >
              Согласен на хранение фото ребёнка
            </Checkbox>
          )}
          {error && <p className={styles.error}>{error}</p>}
          <div className={styles.row}>
            <Button
              variant="secondary"
              onClick={() => {
                setPreview(null);
                if (inputRef.current) inputRef.current.value = "";
              }}
              disabled={busy}
            >
              Отмена
            </Button>
            <Button block onClick={upload} disabled={busy}>
              {busy ? "Загружаем…" : "Загрузить"}
            </Button>
          </div>
        </>
      ) : (
        <>
          {error && <p className={styles.error}>{error}</p>}
          <Button
            block
            variant="secondary"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
          >
            {props.currentPhotoUrl ? "Заменить фото" : "Добавить фото ребёнка"}
          </Button>
        </>
      )}
    </div>
  );
}
